<template>
    <div 
        :id="id" 
        :class="['modal', 'fade', { show: shown }]"
        tabindex="-1"
        role="dialog"
        :style="{ display: shown ? 'block' : 'none' }"
        :aria-labelledby="ariaLabelledById"
        :aria-hidden="!shown"
        aria-modal="true"
        v-on:keydown.esc="handleEscape"
        @click.self="onBackdropClick"
        ref="modal"
    >
        <div class="modal-dialog" role="document" :class="modalDialogClass">
            <slot name="content">
                <div class="modal-content">
                    <div v-if="!noHeader" class="modal-header">
                        <div class="modal-title" :id="ariaLabelledById">
                            <slot name="header"></slot>
                        </div>
                        <button 
                            v-if="!disableClose" 
                            @click="close" 
                            type="button" 
                            class="close" 
                            data-dismiss="modal" 
                            :aria-label="i18n.close"
                            ref="closeButton"
                        >
                            <slot name="close">
                                <span aria-hidden="true">&times;</span>
                            </slot>
                        </button>
                    </div>
                    <slot></slot>
                    <div v-if="$slots.body" :class="['modal-body']">
                        <slot name="body"></slot>
                    </div>
                    <div v-if="!noFooter" class="modal-footer">
                        <slot name="footer" :close="close" :confirm="confirm">
                            <ui-button 
                                data-dismiss="modal" 
                                :appearance="appearance" 
                                button-type="outline"
                                block
                                uppercase
                                :disabled="disableClose"
                                @click="close"
                                ref="cancelButton"
                            >
                                {{ i18n.cancel }}
                            </ui-button>
                            <ui-button 
                                v-if="handleConfirm" 
                                :appearance="appearance" 
                                block
                                uppercase
                                :disabled="disableConfirm"
                                @click="confirm"
                                ref="confirmButton"
                            >
                                {{ i18n.ok }}
                            </ui-button>
                        </slot>
                    </div>
                </div>
            </slot>
        </div>
    </div>
</template>

<script>
// import Translations from "./insert-me-to-translate.vue";
import { blockScroll, releaseScroll } from '../utils';

export default {
    props: {
        id: {
            type: String,
            required: true,
        },
        shown: {
            type: Boolean,
            required: true,
            default: false,
        },
        handleCancel: {
            type: Function,
            default: null,
        },
        handleConfirm: {
            type: Function,
            default: null,
        },
        disableClose: {
            type: Boolean,
            default: false,
        },
        disableConfirm: {
            type: Boolean,
            default: false,
        },
        noHeader: {
            type: Boolean,
            default: false,
        },
        noFooter: {
            type: Boolean,
            default: false,
        },
        verticallyCentered: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            default: undefined,
            validator: value => [undefined, 'lg'].includes(value),
        },
        appearance: {
            type: String,
            default: 'primary',
            validator: value => ['primary', 'secondary'].includes(value),
        },
    },
    components: {
        UiButton: () => import( /* webpackChunkName: "chunk-ui" */  '../components/ui/ui-button'),
    },
    data() {
        return {
            i18n: {
                ok: gettext('Ok'),
                cancel: gettext('Cancel'),
                close: gettext('Close'),
            },
            // Focus management
            previousActiveElement: null,
            focusableElements: [],
            firstFocusableElement: null,
            lastFocusableElement: null,
        }
    },
    computed: {
        ariaLabelledById() {
            return `${this.id}Label`;
        },
        modalDialogClass() {
            return { 
                'modal-dialog-centered': this.verticallyCentered, 
                [`modal-${this.size}`]: this.size,
            }
        },
    },
    watch: {
        shown: {
            immediate: false,
            handler(newVal) {
                if (newVal) {
                    this.openModal();
                } else {
                    this.closeModal();
                }
            }
        }
    },
    methods: {
        confirm(event) {
            if(this.handleConfirm && typeof this.handleConfirm === 'function') {
                this.handleConfirm(event);
            }

            this.$emit("confirm", event);
        },
        close(event) {
            if(this.handleCancel && typeof this.handleCancel === 'function') {
                this.handleCancel(event);
            }

            this.$emit("cancel", event);  
        },
        handleEscape(event) {
            if (!this.disableClose) {
                event.preventDefault();
                this.close(event);
            }
        },
        toggleBackdrop(visible) {
            const existingBackdrop = document.querySelector(".modal-backdrop");

            if (visible) {
                if (!existingBackdrop) {
                    const backdrop = document.createElement("div");
                    backdrop.className = "modal-backdrop fade show";
                    document.body.appendChild(backdrop);
                }
            } else {
                if (existingBackdrop) {
                    existingBackdrop.remove();
                }
            }
        },
        onBackdropClick() {
            if (!this.disableClose) {
                this.close();
            }
        },
        openModal() {
            blockScroll();
            this.toggleBackdrop(true);
            
            this.$nextTick(() => {
                this.setFocusTrap();
            });
        },

        closeModal() {
            this.removeFocusTrap();
            releaseScroll();
            this.toggleBackdrop(false);
        },
        cleanup() {
            if (this.shown) {
                this.closeModal();
            }
            this.removeFocusTrap();            
        },
        // Gets all focusable elements within the modal (not disable and actually visible)
        getFocusableElements() {
            const selectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
            const elements = this.$refs.modal.querySelectorAll(selectors);
            // offsetParent returns the nearest positioned ancestor element (an element with a CSS position value other than "static")
            return Array.from(elements).filter(el => !el.disabled && el.offsetParent !== null);
        },
        // Sets up the focus trap when modal opens
        setFocusTrap() {
            // Store the element that had focus before modal opened
            this.previousActiveElement = document.activeElement;
            
            this.focusableElements = this.getFocusableElements();
            this.firstFocusableElement = this.focusableElements.length ? this.focusableElements[0] : null;
            this.lastFocusableElement = this.focusableElements.length ? this.focusableElements[this.focusableElements.length - 1]: null;

            // Add event listener for tab key
            this.$refs.modal.addEventListener('keydown', this.handleTabKey);

            // Set initial focus
            this.$nextTick(() => {
                // Focus the close button if available, otherwise the first focusable element
                const initialFocus = this.$refs.closeButton || this.firstFocusableElement;
                if (initialFocus) initialFocus.focus();
            });
        },
        //  Cleans up focus trap when modal closes
        removeFocusTrap() {
            this.$refs.modal.removeEventListener('keydown', this.handleTabKey);

            // Return focus to the element that had it before modal opened
            if (this.previousActiveElement) this.previousActiveElement.focus();
        },
        // Handles tab key navigation within the modal
        handleTabKey(event) {
            if (event.key !== 'Tab') return;

            if (event.shiftKey) {
                // shift + tab keys
                if (document.activeElement === this.firstFocusableElement) {
                    event.preventDefault();
                    if(this.lastFocusableElement) this.lastFocusableElement.focus();
                }
            } 
            else {
                // tab key
                if (document.activeElement === this.lastFocusableElement) {
                    event.preventDefault();
                    if(this.firstFocusableElement) this.firstFocusableElement.focus();
                }
            }
        },
    },
    beforeDestroy() {
      this.cleanup();
    }
}
</script>

<style>
.modal-header .close {
    outline: 0 !important;
}
</style>